// libs
import React, { memo, useCallback } from 'react';
import { useHistory } from 'react-router';

// styles
import {
  CustomSection,
  CustomTitle,
  ItemName,
  ItemSubtext,
  SectionContainer,
  CustomIconContainer,
  Container,
  ImageContainer,
  TopWrapper,
  TopSectionContainer,
  MFAContainer,
  RewardsDetails,
  RewardsIcon,
  RewardsTitle,
  RewardsDetailsContainer,
  CustomTitleRewards,
  RewardsDetailsIcon,
} from './styles';

// icons
import Svgs from 'shared/components/MyAccount/Svgs';

// helpers
import { NavigationHandler } from './NavigationHandler';
import { useConfirmRedirect } from 'shared/components/ConfirmRedirect/Provider/context';
import { isProduction, toKebabCase } from 'shared/helpers/utils';
import { useAuthModalWrapperContext } from 'shared/components/AuthModal/AuthModalWrapperContext';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { getDomain } from 'shared/helpers/app';
import { getEligibleVariantName } from 'shared/components/ExperimentV2/helpers';

// analytics
import { trackAccountSectionClicks } from './analytics';

// types
import { AccountSectionProps } from './types';

// constants
import {
  ACCOUNT_ORDER_CAROUSEL_A,
  ACCOUNT_ORDER_CAROUSEL_AB,
  LOCKED_ICON,
  LOCKED_SUBTITLE,
} from './constants';
import { ROLES, TAB_INDEX } from 'shared/constants';
import { MY_ORDERS_V2 } from 'shared/constants/uri';
import { ORDER_STATUS_MFA } from 'shared/components/MyAccount/constants';

// components
import MFA from 'shared/components/MFA';

function MyAccountSections({ section_data, isLoggedIn }: AccountSectionProps) {
  const { showConfirm } = useConfirmRedirect();
  const { enabled } = useRemoteConfig('bottomSheetLoginConfig');
  const isZendeskEnabled = useRemoteConfig('isZendeskEnabled');
  const postOrderUrlV2Enabled = useRemoteConfig('postOrderUrlV2Enabled');
  const { isEligible: isOrderCarouselEnabled = false, variant: orderCarouselVariant = '' } =
    getEligibleVariantName(ACCOUNT_ORDER_CAROUSEL_AB);
  const isRewardsPageEnabled = useRemoteConfig('rewardsPageEnabled') || false;

  const isCarouselEnabled =
    isLoggedIn && isOrderCarouselEnabled && orderCarouselVariant === ACCOUNT_ORDER_CAROUSEL_A;
  const { showBottomSheetAuth } = useAuthModalWrapperContext();
  const { push } = useHistory();

  const formatAriaLabel = (key: string) => {
    return key.replace(/_/g, ' ');
  };

  const handleKeyDown = (e: React.KeyboardEvent, section: any) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSectionClick(section);
    }
  };

  const onSectionClick = (section: AccountSectionProps) => {
    let link = section.action_url;
    if (section.key === 'ORDERS' && postOrderUrlV2Enabled) {
      link = MY_ORDERS_V2;
    } else if (section.key === 'HELP_CENTER' && !isProduction) {
      link = section.action_url_preprod;
    }
    const navigationParams = {
      identifier: section.key,
      url: `${link}?domain=${getDomain()}`,
      isLoggedIn: isLoggedIn,
      otherData: section.otherData,
      isBottomSheetVisible: enabled,
      showBottomSheetAuth,
      showConfirm,
      push,
    };
    trackAccountSectionClicks(section.key);
    NavigationHandler(navigationParams);
  };

  const getIcon = useCallback((isHidden: boolean | undefined, icon_url = '', name: string) => {
    if (isHidden) return <Svgs name={LOCKED_ICON} />;
    if (icon_url.length)
      return <ImageContainer src={icon_url} alt="icon" placeholderColor="transparent" />;
    return <Svgs name={name} />;
  }, []);

  if (!section_data.isEnabled) return null;

  return (
    <Container>
      <CustomTitle>{section_data.title}</CustomTitle>
      {!!section_data.children?.length &&
        section_data.children.map((section, index) => {
          const isHidden = section.isPrivate && !isLoggedIn;
          const Icon = getIcon(isHidden, section.icon_url, section.key);

          if (section.key === 'HELP_CENTER' && isRewardsPageEnabled && !isLoggedIn) {
            return (
              <React.Fragment key={index}>
                <SectionContainer
                  onClick={() => onSectionClick(section)}
                  onKeyDown={(e) => handleKeyDown(e, section)}
                  tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
                  role={ROLES.LINK}
                  aria-label={formatAriaLabel(section.key)}
                  className="with-rewards"
                >
                  <CustomSection>
                    <ItemName data-at={toKebabCase(section.title)}>{section.title}</ItemName>
                    <ItemSubtext>{!isHidden ? section.subTitle : LOCKED_SUBTITLE}</ItemSubtext>
                  </CustomSection>
                  <CustomIconContainer>{Icon}</CustomIconContainer>
                </SectionContainer>

                <CustomTitleRewards>FLX Rewards</CustomTitleRewards>
                <RewardsDetailsContainer>
                  <RewardsDetails>
                    <RewardsIcon>
                      <RewardsDetailsIcon src="https://asset.footlocker.co.in/footlocker-images/rewards-icon-one.svg" />
                    </RewardsIcon>
                    <RewardsTitle>Earn assured cashback as loyalty points</RewardsTitle>
                  </RewardsDetails>
                  <RewardsDetails>
                    <RewardsIcon>
                      <RewardsDetailsIcon src="https://asset.footlocker.co.in/footlocker-images/rewards-icon-two.svg" />
                    </RewardsIcon>
                    <RewardsTitle>Get a welcome bonus and birthday perks</RewardsTitle>
                  </RewardsDetails>
                  <RewardsDetails>
                    <RewardsIcon>
                      <RewardsDetailsIcon src="https://asset.footlocker.co.in/footlocker-images/rewards-icon-three.svg" />
                    </RewardsIcon>
                    <RewardsTitle>Boost your shot at the next sneaker drop</RewardsTitle>
                  </RewardsDetails>
                </RewardsDetailsContainer>
              </React.Fragment>
            );
          }

          if (section.key === 'HELP_CENTER' && isRewardsPageEnabled && isLoggedIn) {
            return (
              <React.Fragment key={"topContainer" + index}>
                <SectionContainer
                  onClick={() => onSectionClick(section)}
                  onKeyDown={(e) => handleKeyDown(e, section)}
                  tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
                  role={ROLES.LINK}
                  aria-label={formatAriaLabel(section.key)}
                  className="with-rewards"
                >
                  <CustomSection>
                    <ItemName data-at={toKebabCase(section.title)}>{section.title}</ItemName>
                    <ItemSubtext>{!isHidden ? section.subTitle : LOCKED_SUBTITLE}</ItemSubtext>
                  </CustomSection>
                  <CustomIconContainer>{Icon}</CustomIconContainer>
                </SectionContainer>
              </React.Fragment>
            );
          }

          if (section.key === 'CHAT_WITH_US' && !isZendeskEnabled) return null;

          return section.key !== 'ORDERS' ? (
            <SectionContainer
              key={`account-section-${section.key || index}`} 
              onClick={() => onSectionClick(section)}
              onKeyDown={(e) => handleKeyDown(e, section)}
              tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
              role={ROLES.LINK}
              aria-label={formatAriaLabel(section.key)}
            >
              <CustomSection key={`custom-section-${section.key || index}`}>
                <ItemName data-at={toKebabCase(section.title)}>{section.title}</ItemName>
                <ItemSubtext>{!isHidden ? section.subTitle : LOCKED_SUBTITLE}</ItemSubtext>
              </CustomSection>
              <CustomIconContainer>{Icon}</CustomIconContainer>
            </SectionContainer>
          ) : (
            <TopWrapper key={"top-wrapper" + index}>
              <TopSectionContainer
                onClick={() => onSectionClick(section)}
                onKeyDown={(e) => handleKeyDown(e, section)}
                tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
                role={ROLES.LINK}
                aria-label={formatAriaLabel(section.key)}
              >
                <CustomSection>
                  <ItemName data-at={toKebabCase(section.title)}>{section.title}</ItemName>
                  <ItemSubtext>{!isHidden ? section.subTitle : LOCKED_SUBTITLE}</ItemSubtext>
                </CustomSection>
                <CustomIconContainer>{Icon}</CustomIconContainer>
              </TopSectionContainer>
              {isCarouselEnabled && (
                <MFAContainer>
                  <MFA {...ORDER_STATUS_MFA} />
                </MFAContainer>
              )}
            </TopWrapper>
          );
        })}
    </Container>
  );
}

export default memo(MyAccountSections);
